import type {
  Document,
  DocumentTitle,
} from '~publish/legacy/composer/composer/entities/Document'
import type {
  Gif,
  Image,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'

/**
 * A single ordered media attachment on a legacy `Draft`. Mirrors the Zustand
 * `OmniAsset` union (`postSlice/types.ts`) minus `link` (the draft's link
 * attachment is tracked separately) and with `document` widened to admit
 * `DocumentTitle` — the placeholder Draft.document holds while a LinkedIn
 * document upload is still in flight, before it has a url/id.
 */
export type DraftAsset =
  | { kind: 'image'; image: Image }
  | { kind: 'video'; video: Video }
  | { kind: 'gif'; gif: Gif }
  | { kind: 'document'; document: Document | DocumentTitle }

/**
 * Canonical cross-kind order: images, then video, then document, then gif.
 * Matches the additive order `draftMediaToAssets` builds in mapDraftToOmni.ts.
 * Order across kinds has no observable effect in this module's callers today
 * (every consumer reads the derived per-kind fields), but keeps `assets`
 * predictable for future readers of the raw list.
 */
const KIND_ORDER: ReadonlyArray<DraftAsset['kind']> = [
  'image',
  'video',
  'document',
  'gif',
]

function rankOf(kind: DraftAsset['kind']): number {
  return KIND_ORDER.indexOf(kind)
}

/** Image payloads, in order. */
export function imagesOf(assets: DraftAsset[]): Image[] {
  const images: Image[] = []
  for (const asset of assets) {
    if (asset.kind === 'image') images.push(asset.image)
  }
  return images
}

/** Video payloads, in order. */
export function videosOf(assets: DraftAsset[]): Video[] {
  const videos: Video[] = []
  for (const asset of assets) {
    if (asset.kind === 'video') videos.push(asset.video)
  }
  return videos
}

/** The first asset of the given kind, or undefined if none is present. */
export function firstOfKind<K extends DraftAsset['kind']>(
  assets: DraftAsset[],
  kind: K,
): Extract<DraftAsset, { kind: K }> | undefined {
  return assets.find(
    (asset): asset is Extract<DraftAsset, { kind: K }> => asset.kind === kind,
  )
}

/** Assets with every entry of the given kind removed. */
export function removeKind(
  assets: DraftAsset[],
  kind: DraftAsset['kind'],
): DraftAsset[] {
  return assets.filter((asset) => asset.kind !== kind)
}

/**
 * Inserts a single asset at its canonical cross-kind position. Used to add
 * (or move) a single-instance kind (video/document/gif) into an asset list
 * that has already had that kind removed.
 */
export function insertAtCanonicalPosition(
  assets: DraftAsset[],
  asset: DraftAsset,
): DraftAsset[] {
  const rank = rankOf(asset.kind)
  let index = assets.length
  for (let i = 0; i < assets.length; i++) {
    if (rankOf(assets[i].kind) > rank) {
      index = i
      break
    }
  }
  return [...assets.slice(0, index), asset, ...assets.slice(index)]
}

/**
 * Appends a video after any videos already present, at the canonical
 * cross-kind position. Channels whose `MixedMediaCapability.canHaveMultipleVideos`
 * is true use this to hold more than one video; every other caller replaces
 * the single video via `replaceKind` instead.
 */
export function appendVideo(assets: DraftAsset[], video: Video): DraftAsset[] {
  return insertAtCanonicalPosition(assets, { kind: 'video', video })
}

/**
 * Removes the Nth video (by video-only index), leaving every other asset —
 * including any other videos — in its exact existing position. Simpler than
 * `removeImageAt`'s image reflow: a multi-video draft only ever grows via
 * `appendVideo`, so there's no interleaved-reorder history among videos to
 * preserve, just the one entry to drop.
 */
export function removeVideoAt(
  assets: DraftAsset[],
  index: number,
): DraftAsset[] {
  let videoIndex = -1
  return assets.filter((asset) => {
    if (asset.kind !== 'video') return true
    videoIndex++
    return videoIndex !== index
  })
}

/**
 * Replaces the single asset of `kind` — image is excluded, since it has many
 * entries and is managed via the image-specific ops below; see `DraftAsset`
 * for the full set of kinds. `next: null` clears the kind, including every
 * entry when more than one is present.
 *
 * A lone existing entry is swapped in place at its current position, so a
 * manually interleaved video/gif/document survives being replaced (e.g. a
 * placeholder swapped for the uploaded asset). Attaching the kind for the
 * first time, or replacing more than one existing entry at once, falls back
 * to the canonical position — there's no single existing slot to anchor to.
 */
export function replaceKind(
  assets: DraftAsset[],
  kind: DraftAsset['kind'],
  next: DraftAsset | null,
): DraftAsset[] {
  if (next === null) return removeKind(assets, kind)

  const existingIndices: number[] = []
  assets.forEach((asset, index) => {
    if (asset.kind === kind) existingIndices.push(index)
  })

  if (existingIndices.length === 1) {
    const [index] = existingIndices
    return assets.map((asset, i) => (i === index ? next : asset))
  }

  return insertAtCanonicalPosition(removeKind(assets, kind), next)
}

/**
 * Longest run of images shared between the old and new order, matched by
 * reference — the pairs a caller kept untouched while editing the rest.
 * Standard LCS dynamic program; image lists are short so the O(n*m) cost
 * never matters.
 */
function longestCommonImageRun(
  oldImages: Image[],
  newImages: Image[],
): Array<[oldIndex: number, newIndex: number]> {
  const n = oldImages.length
  const m = newImages.length
  const lengths: number[][] = Array.from({ length: n + 1 }, () =>
    Array.from({ length: m + 1 }, () => 0),
  )
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lengths[i][j] =
        oldImages[i] === newImages[j]
          ? lengths[i + 1][j + 1] + 1
          : Math.max(lengths[i + 1][j], lengths[i][j + 1])
    }
  }

  const pairs: Array<[number, number]> = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (oldImages[i] === newImages[j]) {
      pairs.push([i, j])
      i++
      j++
    } else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
      i++
    } else {
      j++
    }
  }
  return pairs
}

/**
 * Maps each old image index to the new index of the "same" image — either
 * kept by reference, or, for a same-length gap between two reference matches
 * (or the list's ends), paired positionally as an in-place edit (e.g.
 * `replaceImageAt`, or `applyImageWorkspaceChanges` swapping in an edited
 * copy). A gap with no counterpart on the new side (a genuine removal) is
 * left unmapped; a gap with no counterpart on the old side (a genuine
 * addition) never needs one.
 */
function alignImages(
  oldImages: Image[],
  newImages: Image[],
): Map<number, number> {
  const matches = longestCommonImageRun(oldImages, newImages)
  const alignment = new Map<number, number>()
  let oldCursor = 0
  let newCursor = 0

  const pairGap = (oldEnd: number, newEnd: number): void => {
    const gapLength = Math.min(oldEnd - oldCursor, newEnd - newCursor)
    for (let k = 0; k < gapLength; k++) {
      alignment.set(oldCursor + k, newCursor + k)
    }
  }

  for (const [oldIndex, newIndex] of matches) {
    pairGap(oldIndex, newIndex)
    alignment.set(oldIndex, newIndex)
    oldCursor = oldIndex + 1
    newCursor = newIndex + 1
  }
  pairGap(oldImages.length, newImages.length)

  return alignment
}

/**
 * Replaces the whole ordered image list. A non-image asset (video/gif/
 * document) keeps its place relative to the images around it: it's re-anchored
 * to sit right before whichever surviving image immediately followed it
 * before this call. If every image that used to follow it is gone, it falls
 * to the end, after every image — the same place a fresh attachment (with no
 * prior neighbor to anchor to) lands.
 *
 * This is what lets a manually interleaved video/gif/document (via
 * `moveAsset`) survive an unrelated image add/remove/replace instead of
 * snapping back to canonical kind order.
 */
export function setImages(assets: DraftAsset[], images: Image[]): DraftAsset[] {
  const oldImages = imagesOf(assets)
  const alignment = alignImages(oldImages, images)

  // For each non-image asset, find the nearest surviving image that used to
  // follow it and note "insert before that new index" — or fall to the end.
  const before = new Map<number, DraftAsset[]>()
  const trailing: DraftAsset[] = []
  let imagesSeen = 0
  for (const asset of assets) {
    if (asset.kind === 'image') {
      imagesSeen++
      continue
    }
    let anchor: number | undefined
    for (let oldIndex = imagesSeen; oldIndex < oldImages.length; oldIndex++) {
      const newIndex = alignment.get(oldIndex)
      if (newIndex !== undefined) {
        anchor = newIndex
        break
      }
    }
    if (anchor === undefined) {
      trailing.push(asset)
    } else {
      const bucket = before.get(anchor)
      if (bucket) bucket.push(asset)
      else before.set(anchor, [asset])
    }
  }

  const result: DraftAsset[] = []
  images.forEach((image, index) => {
    const bucket = before.get(index)
    if (bucket) result.push(...bucket)
    result.push({ kind: 'image', image })
  })
  result.push(...trailing)
  return result
}

export function insertImageAt(
  assets: DraftAsset[],
  index: number,
  image: Image,
): DraftAsset[] {
  const images = imagesOf(assets)
  const nextImages = [...images.slice(0, index), image, ...images.slice(index)]
  return setImages(assets, nextImages)
}

export function appendImage(assets: DraftAsset[], image: Image): DraftAsset[] {
  return setImages(assets, [...imagesOf(assets), image])
}

export function replaceImageAt(
  assets: DraftAsset[],
  index: number,
  image: Image,
): DraftAsset[] {
  const nextImages = imagesOf(assets).map((existing, i) =>
    i === index ? image : existing,
  )
  return setImages(assets, nextImages)
}

export function removeImageAt(
  assets: DraftAsset[],
  index: number,
): DraftAsset[] {
  const nextImages = imagesOf(assets).filter((_, i) => i !== index)
  return setImages(assets, nextImages)
}

/** Drops up to `overflow` images from the end of the list. */
export function truncateImages(
  assets: DraftAsset[],
  overflow: number,
): DraftAsset[] {
  if (overflow <= 0) return assets
  const images = imagesOf(assets)
  const nextImages = images.slice(0, Math.max(0, images.length - overflow))
  return setImages(assets, nextImages)
}

/**
 * Moves the asset at `fromIndex` to `toIndex`, splicing across kinds rather
 * than staying within one. Unlike every other op here, this doesn't restore
 * `KIND_ORDER` afterwards — it's how a caller deliberately breaks canonical
 * order to interleave e.g. a video between two images.
 */
export function moveAsset(
  assets: DraftAsset[],
  fromIndex: number,
  toIndex: number,
): DraftAsset[] {
  const next = [...assets]
  const [moved] = next.splice(fromIndex, 1)
  if (!moved) return assets
  next.splice(toIndex, 0, moved)
  return next
}

/**
 * Re-sequences an already-populated asset list to match `order` — a kind
 * tag per asset, in true attach order. Adds and removes nothing: each tag
 * consumes the next not-yet-placed asset of that kind, preserving each
 * kind's own existing relative order. A tag with no matching asset left is
 * skipped; an asset whose kind `order` doesn't fully account for is appended
 * at the end, in place — so a mismatch only ever affects position, never
 * data.
 */
export function reorderByKindSequence(
  assets: DraftAsset[],
  order: readonly DraftAsset['kind'][],
): DraftAsset[] {
  // Queued by index, not by the asset itself — two attachments can be
  // reference-equal (the same object placed twice), and consuming by index
  // keeps each occurrence a distinct slot instead of collapsing them.
  const queues = new Map<DraftAsset['kind'], number[]>()
  assets.forEach((asset, index) => {
    const queue = queues.get(asset.kind)
    if (queue) {
      queue.push(index)
    } else {
      queues.set(asset.kind, [index])
    }
  })

  const reordered: DraftAsset[] = []
  const consumed = new Set<number>()
  for (const kind of order) {
    const index = queues.get(kind)?.shift()
    if (index !== undefined) {
      reordered.push(assets[index])
      consumed.add(index)
    }
  }
  // Leftovers are appended in their original relative order (not grouped by
  // kind) — an asset that `order` doesn't account for should only ever be
  // displaced by the ones `order` did place, never reshuffled against its
  // other unaccounted-for siblings.
  assets.forEach((asset, index) => {
    if (!consumed.has(index)) reordered.push(asset)
  })

  return reordered
}
